import { useState } from 'react';
import { EarconName, useEarcon } from '../../../hooks/useEarcon';
import { useMounted } from '../../../hooks/useMounted';
import { addBundle } from '../../../locale/createUseTextLocalizer';
import { getTextLocalizer } from '../../../locale/useTextLocalizer';
import { validateTextInput } from '../useInputValidationAdapter';
import bundle from '../NovaComponents-Input-gui-icu.properties';
addBundle(bundle);
let localize;
getTextLocalizer('NovaComponents-Input-gui-icu').then(localizer => {
  localize = localizer;
});

/**
 * Email Validation
 */

export const validateEmailInput = props => {
  let isError = false,
    status = undefined,
    message;
  /**
   * Allowed characters are in the range U+0000 to U+FFFF excluding the following ranges :
     C1 controls in the range U+0080 to U+00A0
    General Punctuation, Layout Controls in the range U+2000 to U+206F
    Surrogates  in the range U+D800 to U+DFFF
    * https://rndconfluence.sas.com/confluence/display/I18N/Internationalization+Guidelines+for+Mail+Address
   */
  const liveRegex = props.liveEmailRegex || /^[A-Za-z0-9_@!.#$%&'*+()\-/=?^_`{|}~\u00A1-\u1FFF\u2070-\uD7FF\uE000-\uFFFF]*$/;
  const emailRegex = props.emailRegex || /^[A-Za-z0-9!.#$%&'*+()\-/=?^_`{|}~\u00A1-\u1FFF\u2070-\uD7FF\uE000-\uFFFF]((\.)?[A-Za-z0-9!.#$%&'*+()\-/=?^_`{|}~\u00A1-\u1FFF\u2070-\uD7FF\uE000-\uFFFF])*\@[A-Za-z0-9-\u00A1-\u1FFF\u2070-\uD7FF\uE000-\uFFFF](?!.*\.$)((\.)?[A-Za-z0-9-\u00A1-\u1FFF\u2070-\uD7FF\uE000-\uFFFF])*\.[\u0000-\uFFFF]{2,63}$/; // eslint-disable-line no-control-regex
  const dotIndex = props.value.indexOf('\u002E'); // unicode for period (.)
  const consecutiveDotsRegex = props.consecutiveDotsRegex || /^(?!.*?\.\.)/;
  const localPart = props.value.slice(0, props.value.indexOf('\u0040'));
  const domainPart = props.value.slice(props.value.indexOf('\u0040') + 1);
  const hasOnlyDigitsRegex = /^[0-9]*$/;
  /**
   * https://stackoverflow.com/questions/13522782/how-can-i-tell-if-a-string-has-any-non-ascii-characters-in-it#:~:text=You%20can%20use%20string.,test()%20to%20achieve%20this.
   */

  const hasNonPrintableAsciiCharacters = value => {
    const ascii = /^[ -~]+$/;
    return !ascii.test(value);
  };
  const commonRegexValidations = email => {
    if (!props.isLive && dotIndex >= 0 && !new RegExp(consecutiveDotsRegex).test(email)) {
      isError = true;
      status = 'alert';
      message = localize('emailValidatorConsecutivePeriods.msg');
      return {
        isError,
        status,
        message
      };
    }
    // check for two @ signs
    if (!props.isLive && props.value.indexOf('\u0040') > -1 && props.value.indexOf('\u0040') !== props.value.lastIndexOf('\u0040') // unicode for @ sign
    ) {
      isError = true;
      status = 'alert';
      message = localize('emailValidatorSingleInstance.msg');
      return {
        isError,
        status,
        message
      };
    }
    if (!props.isLive && (localPart.charAt(0) === '.' || localPart.charAt(localPart.length - 1) === '.')) {
      isError = true;
      status = 'alert';
      message = localize('emailValidator.msg');
      return {
        isError,
        status,
        message
      };
    }
    const dnsLabels = domainPart.split('.');
    let completeDomain = '';
    for (let dnsLabelIndex = 0; dnsLabelIndex < dnsLabels.length; dnsLabelIndex++) {
      if (dnsLabels[dnsLabelIndex].length > 63 || dnsLabels[dnsLabelIndex][0] === '-' || dnsLabels[dnsLabelIndex][dnsLabels[dnsLabelIndex].length - 1] === '-') {
        isError = true;
        status = 'alert';
        message = localize('emailValidator.msg');
        return {
          isError,
          status,
          message
        };
      }
      completeDomain += dnsLabels[dnsLabelIndex];
    }
    const topLevelDomain = dnsLabels.length === 0 ? '' : dnsLabels[dnsLabels.length - 1];
    if (!props.isLive && topLevelDomain !== '' && (completeDomain.length > 255 || hasOnlyDigitsRegex.test(topLevelDomain))) {
      isError = true;
      status = 'alert';
      message = localize('emailValidator.msg');
      return {
        isError,
        status,
        message
      };
    }
    if (props.isLive && (!new RegExp(liveRegex).test(email) || /\s/.test(email))) {
      isError = true;
      status = 'alert';
      message = localize('emailValidator.msg');
      return {
        isError,
        status,
        message
      };
    }
    if (!props.isLive && email.length > 0 && !new RegExp(emailRegex).test(email) || localPart.length > 64 || /\s/.test(email)) {
      isError = true;
      status = 'alert';
      message = localize('emailValidator.msg');
      return {
        isError,
        status,
        message
      };
    }
  };
  const domainHasNonAsciiChars = hasNonPrintableAsciiCharacters(props.value.slice(props.value.indexOf('\u0040') + 1)); // unicode for @ sign

  const localHasNonAsciiChars = hasNonPrintableAsciiCharacters(props.value.slice(0, props.value.indexOf('\u0040')));
  const result = validateTextInput({
    value: props.value,
    isRequired: props.isRequired,
    minLength: props.minLength
  });
  if (result.isError) {
    isError = result.isError;
    status = result.status;
    message = result.message;
    return {
      isError,
      status,
      message
    };
  }
  /**
   * Level 0 email validation for english emails
   */
  commonRegexValidations(props.value);

  /**
   * Level 1 email validation for international emails
   * https://rndconfluence.sas.com/confluence/display/I18N/Internationalization+Guidelines+for+Mail+Address
   *
   * */

  if (domainHasNonAsciiChars || localHasNonAsciiChars) {
    /**
     *  Level 1 validaton for non english emails
     *
     */

    const normalizedULabelString = domainHasNonAsciiChars ? domainPart.normalize('NFC').toLowerCase() : domainPart;

    // validate U Label
    if (normalizedULabelString.charAt(2) === '\u2212' &&
    // unicode for - sign
    normalizedULabelString.charAt(3) === '\u2212' || normalizedULabelString.charAt(0) === 'Mc' || normalizedULabelString.charAt(0) === 'Mn' || normalizedULabelString.charAt(0) === 'Me') {
      isError = true;
      status = 'alert';
      message = localize('emailValidator.msg');
      return {
        isError,
        status,
        message
      };
    }
  }
  return {
    isError,
    status,
    message
  };
};
/**
 * This hook provide functionality to handle events such as onChange,onBlur
 * & onCancel with empty initial value.
 * For those who doesn't wish to use this,
 * @function validateEmailInput is available for consumption
 * @param props
 *
 */
export const useEmailValidationAdapter = props => {
  const [value, setValue] = useState(props.initialValue ?? '');
  const earcon = useEarcon({
    source: EarconName.Error
  });
  const [errors, setErrors] = useState({
    status: undefined,
    message: ''
  });
  const mountedRef = useMounted();
  const handleChange = event => {
    const liveValue = event.value;
    const result = validateEmailInput({
      value: liveValue,
      isLive: true,
      isRequired: props.isRequired,
      emailRegex: props.emailRegex,
      liveEmailRegex: props.liveEmailRegex,
      minLength: props.minLength
    });
    setValue(liveValue);
    result.isError ? setErrors({
      status: result.status,
      message: result.message
    }) : setErrors({
      status: undefined,
      message: undefined
    });
    if (result.status === 'alert') earcon.play();
  };
  const handleCommit = e => {
    // setTimeout is needed here to work with FocusLock.
    setTimeout(() => {
      if (!mountedRef.current) return;
      const result = validateEmailInput({
        value,
        isLive: false,
        isRequired: props.isRequired,
        emailRegex: props.emailRegex,
        liveEmailRegex: props.liveEmailRegex,
        minLength: props.minLength
      });
      result.isError ? setErrors({
        status: result.status,
        message: result.message
      }) : setErrors({
        status: undefined,
        message: undefined
      });
      if (result.status === 'alert' && e?.reason === 'enter') earcon.play();
    }, 0);
  };
  const handleBlur = handleCommit;
  const handleCancel = () => {
    setValue('');
    setErrors({
      status: undefined,
      message: undefined
    });
  };
  return {
    handleChange,
    value,
    errors,
    handleBlur,
    handleCommit,
    handleCancel
  };
};
//# sourceMappingURL=useEmailValidationAdapter.js.map